import React, { useState } from 'react';
import { Calculator, Truck, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// "Calculate delivered price" widget — buyer enters destination, sees item + shipping total
export const DeliveredPriceCalculator = ({ product }) => {
  const [open, setOpen] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');

  const getQuote = async () => {
    if (!postcode.trim() || !suburb.trim() || !state) {
      setError('Enter your suburb, state and postcode');
      return;
    }
    setLoading(true);
    setError('');
    setQuote(null);
    try {
      const res = await fetch(`${API_URL}/api/shipping/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination_postcode: postcode.trim(),
          destination_suburb: suburb.trim(),
          destination_state: state,
          parcel_weight: product?.weight || 1,
          parcel_length: product?.parcel_length || 30,
          parcel_width: product?.parcel_width || 20,
          parcel_height: product?.parcel_height || 10
        })
      });
      const data = await res.json();
      if (data.quotes?.length > 0) {
        const cheapest = [...data.quotes].sort((a, b) => a.price - b.price)[0];
        setQuote(cheapest);
      } else {
        setError("No quotes for that address — you'll see exact shipping at checkout");
      }
    } catch {
      setError("Couldn't fetch a quote right now — exact shipping shows at checkout");
    } finally {
      setLoading(false);
    }
  };

  const total = quote ? (Number(product.price) + Number(quote.price)).toFixed(2) : null;

  return (
    <div className="mx-3 mt-3 bg-white rounded-2xl p-5 border border-[#1a3a2a]/5" data-testid="delivered-price-calculator">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between"
        data-testid="delivered-price-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-[#1a3a2a]">
          <Calculator className="w-4 h-4" /> Calculate delivered price
        </span>
        <span className="text-xs text-[#a8a29e]">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Input value={suburb} onChange={(e) => setSuburb(e.target.value)} placeholder="Suburb" data-testid="dp-suburb-input" />
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="dp-state-select"
            >
              <option value="">State</option>
              {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode" inputMode="numeric" data-testid="dp-postcode-input" />
          </div>
          <Button onClick={getQuote} disabled={loading} className="w-full bg-[#1a3a2a] hover:bg-[#14301f]" data-testid="dp-calculate-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Getting quote…</> : 'Show my total'}
          </Button>
          {error && <p className="text-xs text-amber-700">{error}</p>}
          {quote && (
            <div className="bg-[#f0f7f4] rounded-xl p-4 text-sm" data-testid="dp-result">
              <div className="flex justify-between text-[#57534e]"><span>Item</span><span>${Number(product.price).toFixed(2)}</span></div>
              <div className="flex justify-between text-[#57534e] mt-1">
                <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {quote.service_name || quote.carrier}</span>
                <span>${Number(quote.price).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-[#1a3a2a] border-t border-[#1a3a2a]/10 mt-2 pt-2">
                <span>Total delivered</span><span data-testid="dp-total">${total}</span>
              </div>
              <p className="text-[10px] text-[#a8a29e] mt-2">Plus card processing at checkout. No other buyer fees.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveredPriceCalculator;
